/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { DiffText, DiffToggler, ConfirmButton, Loading } from "@components";
import { useEditorContext } from "features/editor/context/editor";
import { getActiveItemTextarea } from "features/editor/utils/utils";
import { useFetchItemLogs } from "features/editor/hooks/useFetchItemLogs";

import { miniBarStyle, miniBarPosStyle } from "./styles";

const PANEL_HISTORY_DIFF_VISIBLE_KEY = "editor-page-history-diff-visible";

const DateLog = ({ timeStamp }: { timeStamp: string }) => {
  const date = new Date(timeStamp);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    // second: "numeric",
    // timeZoneName: "short",
  };
  const humanReadableDate = date.toLocaleDateString(undefined, options);

  return <span>{humanReadableDate}</span>;
};

export const History = () => {
  const { list, activeItemIndex } = useEditorContext();
  const { translation, original } = list.items[activeItemIndex!] || {};
  const { logs, isFetching, refetch: refreshLogs } = useFetchItemLogs(translation?.id);

  const handleRefreshLogs = (options: any) => {
    if (translation.id === options.detail.translationId) {
      refreshLogs();
    }
  };

  useEffect(() => {
    document.addEventListener("editor:item:refresh", handleRefreshLogs);

    return () => {
      document.removeEventListener("editor:item:refresh", handleRefreshLogs);
    };
  });

  const [isDiffVisible, setIsDiffVisible] = useState(
    () => localStorage.getItem(PANEL_HISTORY_DIFF_VISIBLE_KEY) === "true",
  );

  const onToggleTextMode = () =>
    setIsDiffVisible(prev => {
      localStorage.setItem(PANEL_HISTORY_DIFF_VISIBLE_KEY, String(!prev));
      return !prev;
    });

  const onCopyClick = useCallback(
    (text: string) => {
      const activeItemHtml: HTMLElement = getActiveItemTextarea();

      if (!activeItemHtml) {
        return;
      }
      activeItemHtml.dispatchEvent(
        new CustomEvent("setTranslationValue", {
          detail: {
            value: text,
          },
        }),
      );
    },
    [activeItemIndex],
  );

  if (!logs.length) {
    return <Box sx={{ mb: 1.5 }}>No history log to item.</Box>;
  }

  return (
    <>
      <Box sx={miniBarPosStyle}>
        <Box sx={miniBarStyle}>
          <DiffToggler handleChange={onToggleTextMode} checked={isDiffVisible} />
        </Box>
      </Box>
      {isFetching ? (
        <Loading className="b-loading--tiny" />
      ) : (
        logs.map(log => (
          <Box key={log.id}>
            <Box sx={{ opacity: 0.6, fontSize: 14 }}>
              <DateLog timeStamp={log.logDate} />
              <Box component="span" sx={{ pl: 2 }}>
                {log.userEmail}
              </Box>
            </Box>

            <Box sx={{ mb: 2.5, fontSize: 14, display: "flex" }}>
              <Box sx={{ flex: 1, pr: 2 }}>
                <Box sx={{ fontSize: 12, color: "#838383" }}>Original:</Box>
                {isDiffVisible ? (
                  <DiffText text2={original.text} text1={log.originalText} />
                ) : (
                  <Box>{log.originalText}</Box>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: 12, color: "#838383" }}>
                  Translation: {translation.groupName} : {translation.idInFile}
                </Box>
                <Box>
                  <Box sx={{ whiteSpace: "pre-wrap" }}>{log.text}</Box>
                </Box>
              </Box>
              <Box sx={{ width: 20, pl: 1, pt: 1 }}>
                {log.text ? (
                  <ConfirmButton
                    dialogTitle="Replace translation"
                    dialogText="Do you want to replace the existing translation?"
                    onConfirm={() => onCopyClick(log.text)}
                  >
                    <Box component="span" sx={{ cursor: "pointer", color: "#1976d2" }}>
                      <ClipboardDocumentListIcon width={24} height={24} />
                    </Box>
                  </ConfirmButton>
                ) : null}
              </Box>
            </Box>
          </Box>
        ))
      )}
    </>
  );
};
